import React, { useState, useMemo, useEffect, useContext } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { ConfigContext } from "../context/GlobalContext";
import { EditCustomer, EditInvoice, EditProformaInvoice, EditQuotation } from "../data";
import Currancy from "../Utils/currency.json";
import CountryFlags from "../Utils/countries.json";
import { getImage } from "../Utils/image_helper";

const CustomerCurrencyMigrationModal = ({ show, customers = [], businessCurrency = "INR", onClose }) => {
  const { state, dispatch } = useContext(ConfigContext);
  const [customerCountries, setCustomerCountries] = useState({});
  const [selectedAllCountry, setSelectedAllCountry] = useState("");
  const [saving, setSaving] = useState(false);

  // Setup options with unique countryCode as the value
  const currencyOptions = useMemo(() => {
    return Currancy.CountryCurrencyResponse.map(row => {
      const country = CountryFlags?.CurrencyResponse?.find(c => c.isoAlpha2 === row.countryCode);
      return {
        value: row.countryCode,
        label: `${row.name} (${row.currencyCode} - ${row.currencytSymbol})`,
        flag: country?.flag,
        raw: row
      };
    });
  }, []);

  const formatOptionLabel = ({ flag, raw }, { context }) => (
    <div className="d-flex align-items-center gap-2">
      <img
        src={flag ? `data:image/png;base64,${flag}` : getImage("euro_flag")}
        alt="flag"
        width="20"
        height="12"
        style={{ objectFit: "cover", borderRadius: 1 }}
      />
      <span style={{ fontSize: "13px" }}>
        {context === "value"
          ? `${raw.currencyCode} - ${raw.currencytSymbol}`
          : `${raw.name} (${raw.currencyCode} - ${raw.currencytSymbol})`
        }
      </span>
    </div>
  );

  // Default country code from businessCurrency
  const defaultCountryCode = useMemo(() => {
    const matched = Currancy.CountryCurrencyResponse.find(row => row.currencyCode === businessCurrency);
    return matched ? matched.countryCode : "IN"; // default to India if not found
  }, [businessCurrency]);

  // Initialize selected country for customers when modal opens or customer list changes
  useEffect(() => {
    if (show && customers && customers.length > 0) {
      const initial = {};
      customers.forEach(cust => {
        let code = cust.countryCode;
        if (!code && cust.currency) {
          const matched = Currancy.CountryCurrencyResponse.find(row => row.currencyCode === cust.currency);
          if (matched) code = matched.countryCode;
        }
        if (!code) {
          code = defaultCountryCode;
        }
        initial[cust.customerId] = code;
      });
      setCustomerCountries(initial);

      // Initialize selectedAllCountry based on first customer
      let firstCustCode = customers[0].countryCode;
      if (!firstCustCode && customers[0].currency) {
        const matched = Currancy.CountryCurrencyResponse.find(row => row.currencyCode === customers[0].currency);
        if (matched) firstCustCode = matched.countryCode;
      }
      setSelectedAllCountry(firstCustCode || defaultCountryCode);
    }
  }, [show, customers, defaultCountryCode]);

  const handleSelectAllChange = (selectedOption) => {
    const newCountry = selectedOption?.value || "";
    setSelectedAllCountry(newCountry);
    setCustomerCountries(prev => {
      const updated = { ...prev };
      customers.forEach(cust => {
        updated[cust.customerId] = newCountry;
      });
      return updated;
    });
  };

  const handleCustomerCountryChange = (customerId, selectedOption) => {
    setCustomerCountries(prev => ({
      ...prev,
      [customerId]: selectedOption?.value || ""
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const customer of customers) {
        const selectedCountryCode = customerCountries[customer.customerId] || defaultCountryCode;
        const matchedOption = currencyOptions.find(opt => opt.value === selectedCountryCode);
        if (!matchedOption) continue;

        const selectedBusiness = state?.BUSINESSES?.find(b => String(b.companyId) === String(customer.companyId));
        const targetUid = selectedBusiness?.isShared ? selectedBusiness.ownerUid : state?.USERID;

        const updatedPaymentsReceived = (customer.paymentsReceived || []).map(payment => ({
          ...payment,
          currency: payment.currency || matchedOption.raw.currencyCode
        }));

        // 1. Update customer profile
        await EditCustomer(state, dispatch, {
          ...customer,
          currency: matchedOption.raw.currencyCode,
          paymentsReceived: updatedPaymentsReceived,
          // country: matchedOption.raw.name,
          // countryCode: matchedOption.raw.countryCode,
          updatedAt: Date.now().toString(),
        }, targetUid);

        // 2. Update related invoices
        const relatedInvoices = (state.INVOICES || []).filter(
          inv => String(inv.customerId) === String(customer.customerId)
        );
        for (const inv of relatedInvoices) {
          if (!inv.currency) {
            await EditInvoice(state, dispatch, inv.uniqueID, {
              ...inv,
              // dateDue: inv.dateDue || inv.dueDate,
              currency: matchedOption.raw.currencyCode,
              // country: matchedOption.raw.name,
              // countryCode: matchedOption.raw.countryCode,
            }, targetUid);
          }
        }

        // 3. Update related proforma invoices
        const relatedProformas = (state.PROFORMAINVOICES || []).filter(
          inv => String(inv.customerId) === String(customer.customerId)
        );
        for (const inv of relatedProformas) {
          if (!inv.currency) {
            await EditProformaInvoice(state, dispatch, inv.uniqueID, {
              ...inv,
              // dateDue: inv.dateDue || inv.dueDate,
              currency: matchedOption.raw.currencyCode,
              // country: matchedOption.raw.name,
              // countryCode: matchedOption.raw.countryCode,
            }, targetUid);
          }
        }

        // 4. Update related quotations
        const relatedQuotations = (state.QUOTATION || []).filter(
          inv => String(inv.customerId) === String(customer.customerId)
        );
        for (const inv of relatedQuotations) {
          if (!inv.currency) {
            await EditQuotation(state, dispatch, inv.uniqueID, {
              ...inv,
              // dateDue: inv.dateDue || inv.dueDate,
              currency: matchedOption.raw.currencyCode,
              // country: matchedOption.raw.name,
              // countryCode: matchedOption.raw.countryCode,
            }, targetUid);
          }
        }
      }
      toast.success("Customer currencies and documents updated successfully!");
      if (onClose) onClose();
    } catch (error) {
      console.error("Error saving customer currencies and documents:", error);
      toast.error("Failed to update some records. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "var(--neutral-50)",
      borderColor: "var(--border-color)",
      color: "var(--text-secondary-light)",
      boxShadow: "none",
      minHeight: "38px",
      height: "38px",
      borderRadius: "8px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 10px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "36px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--neutral-50)",
      border: "1px solid var(--border-color)",
      zIndex: 9999,
      width: "max-content",
      minWidth: "100%",
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text-secondary-light)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--text-secondary-light)",
    }),
    input: (base) => ({
      ...base,
      color: "var(--text-secondary-light)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? "var(--neutral-200)"
        : "transparent",
      color: "var(--text-secondary-light)",
      cursor: "pointer",
    }),
  };

  if (!show || customers.length === 0) return null;

  // Derive the business name from the first customer's companyId
  const businessCompanyId = customers[0]?.companyId;
  const selectedBusiness = state?.BUSINESSES?.find(
    b => String(b.companyId) === String(businessCompanyId)
  );
  const businessName = selectedBusiness?.companyName || null;

  const currentSelectAllValue = currencyOptions.find(opt => opt.value === selectedAllCountry);

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>

      <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1060, overflowY: "auto" }}>
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content shadow border-0 rounded-4" style={{ overflow: "visible" }}>

            {/* Modal Header */}
            <div className="modal-header d-flex flex-column align-items-start gap-1">
              <div className="d-flex align-items-center justify-content-between w-100">
                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0">
                  <i className="bi bi-currency-exchange text-primary fs-4"></i>
                  Set Customer Currency
                </h5>
                {/* <button
                  type="button"
                  className="btn-close"
                  onClick={onClose}
                  disabled={saving}
                ></button> */}
              </div>
              {businessName && (
                <div className="d-flex align-items-center gap-2 mt-1">
                  <span className="text-secondary-light fs-13 fw-medium">Business:</span>
                  <span
                    className="badge rounded-pill px-12 py-6 fw-semibold fs-12 d-flex align-items-center gap-1"
                    style={{
                      backgroundColor: "var(--primary-50, #eff4ff)",
                      color: "var(--primary-600, #487fff)",
                      border: "1px solid var(--primary-100, #c9d9ff)"
                    }}
                  >
                    <i className="bi bi-building" style={{ fontSize: "11px" }}></i>
                    {businessName}
                  </span>
                </div>
              )}
            </div>

            {/* Modal Body */}
            <div className="modal-body px-24 py-20" style={{ overflow: "visible" }}>
              <p className="text-secondary-light mb-4 fs-14 fw-medium bg-light p-3 rounded border border-neutral-200 text-center">
                We've moved from a global currency to customer-based currencies. Please select a currency for each customer. All invoices will be saved in selected currency for that customer.
              </p>

              {/* Title and Select for all (aligned right) */}
              <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                <div className="fw-bold text-secondary-light fs-14 mb-0">Customers Checklist</div>
                <div className="d-flex align-items-center gap-2">
                  <label className="fw-semibold text-secondary-light fs-13 mb-0" style={{ whiteSpace: "nowrap" }}>
                    Select for all:
                  </label>
                  <div style={{ width: "160px" }}>
                    <Select
                      options={currencyOptions}
                      value={currentSelectAllValue}
                      onChange={handleSelectAllChange}
                      formatOptionLabel={formatOptionLabel}
                      styles={customSelectStyles}
                      placeholder="Select"
                      isDisabled={saving}
                      isSearchable={true}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
              </div>

              {/* List of customers */}
              <div
                className="hide-scrollbar border rounded border-neutral-200 p-3 bg-light"
                style={{ maxHeight: "350px", overflowY: "auto" }}
              >
                <div className="row g-3">
                  {customers.map((customer, idx) => {
                    const customerCountry = customerCountries[customer.customerId] || defaultCountryCode;
                    const selectedVal = currencyOptions.find(opt => opt.value === customerCountry);

                    return (
                      <div key={customer.customerId} className="col-md-4 col-12">
                        <div className="card h-100 shadow-sm border border-neutral-200 rounded-3 p-3 bg-white">
                          <div className="row align-items-center h-100">
                            <div className="col-6">
                              <div className="d-flex flex-column">
                                <span className="fw-bold text-dark fs-14 text-truncate" style={{ display: "block" }} title={customer.customer}>
                                  {customer.customer}
                                </span>
                                <span className="text-muted fs-11 text-truncate" style={{ display: "block" }} title={customer.email || ""}>
                                  {customer.email || <>&nbsp;</>}
                                </span>
                                <span className="text-muted fs-11 text-truncate" style={{ display: "block" }} title={customer.phone || ""}>
                                  {customer.phone ? `${customer.dialCode || ""} ${customer.phone}` : <>&nbsp;</>}
                                </span>
                              </div>
                            </div>
                            <div className="col-6">
                              <Select
                                options={currencyOptions}
                                value={selectedVal}
                                onChange={(opt) => handleCustomerCountryChange(customer.customerId, opt)}
                                formatOptionLabel={formatOptionLabel}
                                styles={customSelectStyles}
                                placeholder="Select"
                                isDisabled={saving}
                                isSearchable={true}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer border-0 px-24 py-10 bg-light">
              {/* <button
                type="button"
                className="btn btn-secondary border-neutral-300 fs-13 py-8 px-16 fw-semibold text-secondary-light animate-all"
                onClick={onClose}
                disabled={saving}
              >
                Close
              </button> */}
              <button
                type="button"
                className="btn btn-primary fs-13 py-8 px-20 fw-semibold d-flex align-items-center gap-2 animate-all"
                onClick={handleSave}
                disabled={saving || customers.length === 0}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Saving Changes...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerCurrencyMigrationModal;
